import React, { useCallback, useEffect, useState } from 'react';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  TextField,
  Typography,
} from '@mui/material';
import {
  getFanEnergyApiPayload,
  getFanEnergyDailyLeaderboard,
  isFanEnergyApiSuccess,
} from '../../services/fanEnergyApi';
import LeaderboardTable from './LeaderboardTable';
import { formatNumber, getTodayIst, PAGE_SIZE } from './fanEnergyLeaderboardUtils';

function DailyLeaderboardTab() {
  const [dayInput, setDayInput] = useState(getTodayIst());
  const [day, setDay] = useState(getTodayIst());
  const [offset, setOffset] = useState(0);
  const [entries, setEntries] = useState([]);
  const [responseDay, setResponseDay] = useState(null);
  const [pagination, setPagination] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const loadLeaderboard = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const result = await getFanEnergyDailyLeaderboard({ day, limit: PAGE_SIZE, offset });
      if (!isFanEnergyApiSuccess(result)) {
        setError(result?.message || 'Failed to load daily leaderboard.');
        setEntries([]);
        setResponseDay(null);
        setPagination(null);
        return;
      }
      const data = getFanEnergyApiPayload(result);
      setEntries(data?.entries || []);
      setResponseDay(data?.day || day);
      setPagination(data?.pagination || null);
    } catch (fetchError) {
      setError(fetchError.message || 'Failed to load daily leaderboard.');
      setEntries([]);
    } finally {
      setLoading(false);
    }
  }, [day, offset]);

  useEffect(() => {
    loadLeaderboard();
  }, [loadLeaderboard]);

  const applyDay = () => {
    setDay(dayInput);
    setOffset(0);
  };

  return (
    <>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', mb: 2 }}>
        <TextField
          size="small"
          label="Day (IST)"
          type="date"
          value={dayInput}
          InputLabelProps={{ shrink: true }}
          onChange={(event) => setDayInput(event.target.value)}
          sx={{ width: 180 }}
        />
        <Button variant="contained" onClick={applyDay} disabled={loading || !dayInput}>
          Apply
        </Button>
        <Button
          variant="text"
          startIcon={<RefreshIcon />}
          onClick={loadLeaderboard}
          disabled={loading}
        >
          Refresh
        </Button>
      </Box>

      {responseDay && (
        <Chip label={`IST day · ${responseDay}`} color="primary" variant="outlined" sx={{ mb: 1.5 }} />
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {pagination
              ? `${formatNumber(pagination.total)} entr${pagination.total === 1 ? 'y' : 'ies'}`
              : 'Leaderboard'}
            {' · registered users only · net FE for the IST day'}
          </Typography>
          <LeaderboardTable entries={entries} showGuestBadge={false} />
          <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <Button disabled={loading || offset === 0} onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}>
              Previous
            </Button>
            <Button disabled={loading || !pagination?.hasMore} onClick={() => setOffset(offset + PAGE_SIZE)}>
              Next
            </Button>
            <Typography variant="body2" color="text.secondary">
              Offset {offset}
            </Typography>
          </Box>
        </>
      )}
    </>
  );
}

export default DailyLeaderboardTab;
